<template>
    <div class="customer-search">
        <loader class="panel-loader" v-if="!isDataLoaded"></loader>
        <div class="error-container alert alert-danger" v-if="isDataLoaded && hasError">
            {{error}}
        </div>
        <div class="search-container" v-if="isDataLoaded">
            <div class="customer-search-form sliding-tray" :class="formClass">
                <div class="sliding-tray-content">
                    <div class="scope form-group">
                        <vue-chosen v-model="scope" :options="availableScopes"></vue-chosen>
                    </div>
                    <div class="form-group customer-search-query-wrapper">
                        <component v-bind:is="searchMode" v-model="search" :searchSpec="queryBuilderSpec" :class="searchClass" />
                    </div>
                    <div class="form-group">
                        <button class="search-mode btn btn-primary col-xs-3" v-on:click="toggleMode()">{{toggleTo}}</button>
                        <button class="search-action btn btn-primary col-xs-3 pull-right" v-on:click="submitSearch()">Search</button>
                    </div>
                    <div class="form-group">
                        <toggle-switch v-model="save.save">Save for Later</toggle-switch>
                    </div>
                    <div class="form-group" v-if="save.save">
                        <label>Name:</label>
                        <input class="form-control" v-model="save.name" />
                        <label>Description:</label>
                        <text-area-component class="form-control" v-model="save.description" />
                    </div>
                </div>
                <button v-on:click="toggleForm()" class="sliding-tray-toggle"><span>Search</span></button>
            </div>
            <div class="customer-search-results" v-if="hasResults">
                <component v-bind:is="resultDisplay" :data="results" :columns="columns" :options="options" v-on:data:remove="trimResults"/>
            </div>
            <div class="customer-search-no-results" v-if="!hasResults">
                0 matching results
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import querystring from 'querystring'
    import QueryBuilder from '@/components/search/QueryBuilder'
    import SortableTable from '@/components/SortableTable'
    import BulkUpdate from '@/widgets/BulkUpdate'
    import TextAreaComponent from '@/components/TextAreaComponent'
    import VueChosen from '@/components/VueChosen'
    import ToggleSwitch from '@/components/ToggleSwitch'
    import Loader from '@/components/Loader'

    export default {
        props: ['contentSource', 'mode'],
        name: 'CustomerSearch',
        components: {
            ToggleSwitch,
            QueryBuilder,
            SortableTable,
            BulkUpdate,
            TextAreaComponent,
            VueChosen,
            Loader
        },
        data () {
            return {
                content: false,
                options: null,
                displayMode: false,
                results: null,
                columns: null,
                queryBuilderSpec: null,
                formVisible: true,
                save: {
                    save: false,
                    name: '',
                    description: ''
                },
                search: '',
                scope: null,
                availableScopes: null,
                error: null
            }
        },
        computed: {
            isDataLoaded () {
                return this.content || false
            },
            hasResults () {
                return Array.isArray(this.results) && this.results.length
            },
            hasError () {
                return this.error !== undefined && this.error !== null
            },
            showAdvancedSearch () {
                return (typeof this.search === 'object')
            },
            searchMode () {
                return this.showAdvancedSearch ? 'query-builder' : 'text-area-component'
            },
            searchClass () {
                return this.showAdvancedSearch ? '' : 'form-control'
            },
            toggleTo () {
                return this.showAdvancedSearch ? 'Basic' : 'Query Builder'
            },
            resultDisplay () {
                return this.displayMode === 'bulk' ? 'bulk-update' : 'sortable-table'
            },
            formClass () {
                const formClass = []

                formClass.push(this.formVisible ? 'sliding-tray-open' : '')

                if (this.displayMode === 'bulk') {
                    formClass.push('col-xs-4')
                } else {
                    formClass.push('col-xs-11')
                }

                return formClass.join(' ')
            }
        },
        methods: {
            toggleForm () {
                this.formVisible = !this.formVisible
            },
            toggleMode () {
                if (this.search === '' || Object.keys(this.search).length === 0 || window.confirm('Changing modes will discard your current search. Continue?')) {
                    this.search = this.showAdvancedSearch ? '' : {}
                }
            },
            submitSearch () {
                this.getData({
                    searchTerm: typeof this.search === 'string' ? this.search : JSON.stringify(this.search),
                    searchScope: this.scope,
                    save: JSON.stringify(this.save)
                })
            },
            getData (query) {
                this.content = false
                let self = this
                return axios.post(this.contentSource, querystring.stringify(query), {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(function (response) {
                        if (!response.data.result) {
                            response.data.error = response.data.message || 'An unknown error has occurred'
                        }
                        self.setData(self, response.data)
                    })
                    .catch((error) => {
                        if (error instanceof Error) {
                            self.setData({error: error.message || 'An unknown error has occurred'})
                        }
                    })
            },
            setData (self, data) {
                self.content = true
                self.queryBuilderSpec = data.queryBuilderSpec || self.queryBuilderSpec || null
                self.options = data.options || self.options || ''
                self.search = data.search || self.search || ''
                self.availableScopes = data.availableScopes || self.availableScopes || {
                    Group: [
                        {
                            id: 'MINE',
                            label: 'Mine'
                        },
                        {
                            id: 'TEAM',
                            label: 'My Team'
                        },
                        {
                            id: 'GLOBAL',
                            label: 'Global'
                        }
                    ]
                }
                self.scope = data.scope || self.scope || 'GLOBAL'
                self.columns = data.columns || self.columns || {}
                self.results = data.results || self.results || null
                self.error = data.error || null
                self.save = {
                    save: false,
                    name: '',
                    description: ''
                }

                if (this.hasResults || this.hasError) {
                    this.formVisible = false
                }
            },
            trimResults (event) {
                this.results = this.results.filter(function (element) {
                    return event.indexOf(element.id) < 0
                })
            }
        },
        created () {
            if (this.contentSource !== undefined && this.contentSource !== null) {
                this.getData({})
            }

            if (this.mode === undefined || this.mode === null) {
                this.displayMode = 'basic'
            } else {
                this.displayMode = this.mode
            }
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>

</style>



// WEBPACK FOOTER //
// CustomerSearch.vue?7b7ec516